import React, { useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { FilterList } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { ProjectsTable } from '@/components/projects/ProjectsTable'
import { ProjectFilters } from '@/components/projects/ProjectFilters'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { Project, ProjectFilter } from '@/types'
import { UPDATE_USER_FAVORITES } from '@/graphql/mutations'
import { LIST_PROJECTS } from '@/graphql/queries'

export const ProjectsPage: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [filter, setFilter] = useState<ProjectFilter>({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const [updateFavorites] = useMutation(UPDATE_USER_FAVORITES, {
    refetchQueries: [{ query: LIST_PROJECTS, variables: { filter, limit: 500 } }],
  })

  const handleFilterChange = (newFilter: ProjectFilter) => {
    setFilter(newFilter)
  }

  const handleProjectSelect = (project: Project) => {
    console.log('Selected project:', project)
  }

  const handleToggleFavorite = async (projectId: string, isFavorite: boolean) => {
    if (!user) return
    try {
      await updateFavorites({
        variables: { input: { userId: user.id, projectId, isFavorite } },
      })
    } catch (error) {
      console.error('Error toggling favorite:', error)
    }
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <IconButton
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          color={isFilterOpen ? 'primary' : 'default'}
          title={isFilterOpen ? t('projects.filters.hide') : t('projects.filters.show')}
          sx={{ border: 1, borderColor: isFilterOpen ? 'primary.main' : 'divider' }}
        >
          <FilterList />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          {t('projects.filters.accessFilters')}
        </Typography>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Filters Sidebar */}
        <Box sx={{
          width: isFilterOpen ? 280 : 0,
          flexShrink: 0,
          borderRight: isFilterOpen ? 1 : 0,
          borderColor: 'divider',
          pr: isFilterOpen ? 2 : 0,
          mr: isFilterOpen ? 2 : 0,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
        }}>
          <ProjectFilters onFilterChange={handleFilterChange} />
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <ProjectsTable
            filter={filter}
            onProjectSelect={handleProjectSelect}
            onToggleFavorite={handleToggleFavorite}
          />
        </Box>
      </Box>
    </Box>
  )
}
